import React, { useState } from "react";
import clsx from "clsx";
import startCase from "lodash/startCase";
import { Accordion, Badge, Text, Tooltip } from "@merge-api/merge-javascript-shared";
import { ChevronDown } from "lucide-react";
import { EnabledAction, ScopeOption } from "../../../../../../models/CommonModel";
import { APICategory } from "../../../../../../models/Entities";
import {
  getRelatedFieldFromField,
  getRelatedModelFromField,
  ModelWithSortedFields,
  SortedModelsMap,
} from "../../CommonModelUtils";
import {
  DOCS_REMOTE_DATA_PATH,
  DOCS_REMOTE_FIELDS_PATH,
} from "../../../../../../router/RouterUtils";
import { pascalCaseToTitleCase } from "../../../../../../utils/casing";
import {
  NON_FIELD_TOGGLES,
  REMOTE_DATA_DESCRIPTION,
  REMOTE_DATA_FIELD,
  REMOTE_DATA_INFO_TEXT,
  REMOTE_FIELDS_DESCRIPTION,
  REMOTE_FIELDS_FIELD,
  REMOTE_FIELDS_INFO_TEXT,
  REMOTE_FIELD_CLASS_MODEL,
  SCOPE_COLUMN_CLASS,
  scopeDisabledSubObjectMessage,
} from "../constants";
import { scopeForField, scopeForModelCapability } from "../utils/scopeMapping";
import { getFieldDeprecation, getModelDeprecation, isFieldHidden } from "../utils/deprecation";
import { isPastDeprecation, resolveDeprecationMessage } from "../utils/resolveDeprecationMessage";
import ScopeFieldRow from "./ScopeFieldRow";
import ScopePermissionSwitch from "./ScopePermissionSwitch";
import ScopeRelationNote from "./ScopeRelationNote";
import ScopeRemoteRow from "./ScopeRemoteRow";

interface Props {
  model: ModelWithSortedFields;
  /** All models in the category, for resolving sub-object references. */
  models: SortedModelsMap;
  /** Last-saved model state; deprecated-field visibility keys off this, not the in-flight edit. */
  savedModels: SortedModelsMap;
  category: APICategory;
  showDeprecatedSunsetItems: boolean;
  /** First row in the table: skip the top border since the header already has a bottom border. */
  isFirst?: boolean;
  isViewing: boolean;
  areOptionalScopesEnabled: boolean;
  disableObjectScopes: boolean;
  disableFieldScopes: boolean;
  /** True when Remote Data / Remote Fields can't be toggled (plan or org restriction). */
  disableRemoteScopes: boolean;
  /** Tooltip explaining why object scope switches are disabled. */
  objectScopesDisabledTooltip?: React.ReactNode;
  /** Tooltip explaining why field scope switches are disabled (may include an upgrade link). */
  fieldScopesDisabledTooltip?: React.ReactNode;
  /** Tooltip explaining why the Remote toggles are disabled (may include an upgrade link). */
  remoteScopesDisabledTooltip?: React.ReactNode;
  onModelScopeChange: (capability: EnabledAction, scope: ScopeOption) => void;
  onFieldScopeChange: (modelName: string, fieldName: string, scope: ScopeOption) => void;
  onRemoteDataChange: (scope: ScopeOption) => void;
  onRemoteFieldsChange: (scope: ScopeOption) => void;
}

const CAPABILITY_COLUMNS = [
  { capability: EnabledAction.READ, columnClass: SCOPE_COLUMN_CLASS.read },
  { capability: EnabledAction.WRITE, columnClass: SCOPE_COLUMN_CLASS.write },
];

/** One Common Model as an expandable accordion row: Read/Write controls + its fields. */
const ScopeObjectRow = ({
  model,
  models,
  savedModels,
  category,
  showDeprecatedSunsetItems,
  isFirst,
  isViewing,
  areOptionalScopesEnabled,
  disableObjectScopes,
  disableFieldScopes,
  disableRemoteScopes,
  objectScopesDisabledTooltip,
  fieldScopesDisabledTooltip,
  remoteScopesDisabledTooltip,
  onModelScopeChange,
  onFieldScopeChange,
  onRemoteDataChange,
  onRemoteFieldsChange,
}: Props) => {
  // state
  const [isExpanded, setIsExpanded] = useState(false);

  // derived state
  // A field's visibility keys off its saved enabled state (so disabling it in edit mode doesn't
  // make it vanish mid-edit) and its OWN deprecation only — inheriting the model's deprecation
  // never hides a field, so an enabled deprecated model still shows all of its fields.
  const savedModel = savedModels[model.name] ?? model;
  const fields = Object.values(model.fields).filter(
    (field) =>
      !NON_FIELD_TOGGLES.includes(field.field_name) &&
      !isFieldHidden(
        category,
        model.name,
        field.field_name,
        savedModel.fields[field.field_name]?.is_enabled ?? field.is_enabled,
        showDeprecatedSunsetItems,
      ),
  );
  const enabledCount = fields.filter((field) => field.is_enabled).length;

  // Model-level deprecation: a red "Deprecated" badge whose tooltip carries the date-driven message.
  // The message + past/future treatment also cascade onto the model's fields and remote rows.
  const modelDeprecation = getModelDeprecation(category, model.name);
  const modelDeprecationMessage = resolveDeprecationMessage(modelDeprecation);
  const modelIsPastDeprecation = isPastDeprecation(modelDeprecation);

  // Remote toggles: remote_data is a field on this model; remote_fields reflects the global
  // RemoteFieldClass model's enabled state (toggling it toggles that model, not a field here).
  const remoteDataField = model.fields[REMOTE_DATA_FIELD];
  const remoteFieldsField = model.fields[REMOTE_FIELDS_FIELD];
  const remoteDataScope = remoteDataField?.is_enabled ? ScopeOption.ENABLED : ScopeOption.DISABLED;
  const remoteFieldsScope =
    (models[REMOTE_FIELD_CLASS_MODEL]?.enabled_model_actions?.length ?? 0) > 0
      ? ScopeOption.ENABLED
      : ScopeOption.DISABLED;

  // render
  return (
    // id anchors this row so a related field's relation note can scroll the user here.
    <div id={model.name} className={clsx("scroll-mt-4", !isFirst && "border-t border-gray-10")}>
      <Accordion
        variant="none"
        hideChevron
        expanded={isExpanded}
        setExpanded={setIsExpanded}
        titleClassName="px-4 py-3 cursor-pointer hover:bg-gray-0 min-h-0 [&.Mui-expanded]:min-h-0 [&_.MuiAccordionSummary-content]:my-0 [&_.MuiAccordionSummary-expandIconWrapper]:hidden"
        title={
          // items-start + a 24px (h-6) band on each control cell aligns the pills / count to the
          // name's FIRST line: the sm switch pill is h-6 and h6's line-height is 24px, so a
          // single-line row reads centered and a wrapped name keeps them pinned to line one. The
          // expand chevron is the exception, self-stretching to center in the full row (see below).
          <div className="flex w-full items-start">
            <div className={clsx(SCOPE_COLUMN_CLASS.object, "flex items-center")}>
              {/* No tooltip on the name; a deprecated model's message lives on the badge instead.
                  The name wraps instead of truncating (a single line is naturally h-6 tall, so its
                  first line aligns with the h-6 control bands under items-start). */}
              <Text variant="h6">{pascalCaseToTitleCase(model.name)}</Text>
            </div>
            {CAPABILITY_COLUMNS.map(({ capability, columnClass }) => (
              <div key={capability} className={clsx(columnClass, "flex h-6 items-center")}>
                {model.model_capabilities.includes(capability) && (
                  <ScopePermissionSwitch
                    scope={scopeForModelCapability(model, capability, areOptionalScopesEnabled)}
                    isViewing={isViewing}
                    areOptionalScopesEnabled={areOptionalScopesEnabled}
                    disabled={disableObjectScopes}
                    disabledTooltip={objectScopesDisabledTooltip}
                    onChange={(scope) => onModelScopeChange(capability, scope)}
                  />
                )}
              </div>
            ))}
            {/* Own column for the model's "Deprecated" badge so it never crowds the field count.
                The deprecation message shows on hover over the badge (leaveDelay + white link so a
                "Use Collection instead"-style link is readable and clickable on the dark tooltip). */}
            <div
              className={clsx(SCOPE_COLUMN_CLASS.status, "flex h-6 items-center justify-end pr-3")}
            >
              {modelDeprecation.deprecationStage && (
                <Tooltip
                  leaveDelay={150}
                  title={<span className="[&_a]:text-white">{modelDeprecationMessage}</span>}
                >
                  <span className="inline-flex">
                    <Badge color="red">Deprecated</Badge>
                  </span>
                </Tooltip>
              )}
            </div>
            <div className={clsx(SCOPE_COLUMN_CLASS.fields, "flex h-6 items-center text-gray-40")}>
              <Text variant="md" className="whitespace-nowrap tabular-nums text-gray-50">
                {enabledCount}/{fields.length} enabled
              </Text>
            </div>
            {/* Expand chevron in its own column, aligned under the header's docs link. Unlike the
                other control cells (pinned to the first line), it self-stretches to the full row
                height and centers, so on a wrapped two-line row it sits centered as a row-level
                expand affordance. */}
            <div
              className={clsx(
                SCOPE_COLUMN_CLASS.expand,
                "flex items-center justify-end self-stretch",
              )}
            >
              <ChevronDown
                size={16}
                className={clsx("text-black transition-transform", isExpanded && "rotate-180")}
              />
            </div>
          </div>
        }
      >
        <div className="divide-y divide-gray-10 border-t border-gray-10 bg-gray-0/40">
          {fields.map((field) => {
            const ownFieldDeprecation = getFieldDeprecation(category, model.name, field.field_name);
            // A field inside a deprecated model inherits the model's deprecation (date-driven
            // message + past/future treatment) unless the field has its own.
            const fieldDeprecation = ownFieldDeprecation.deprecationStage
              ? ownFieldDeprecation
              : modelDeprecation;
            // related_to is "ModelName.fieldName"; split to resolve the referenced model.
            const relatedModelName = getRelatedModelFromField(field) || undefined;
            const relatedModel = relatedModelName ? models[relatedModelName] : undefined;
            const isSelfRelated = relatedModelName === model.name;
            // Sub-object: related model has no endpoints, so it expands inline here.
            const isSubObject = !!relatedModel && !relatedModel.has_endpoints && !isSelfRelated;
            const relatedSubModel = isSubObject ? relatedModel : undefined;
            // Standalone relation: related model is its own top-level object; show a note linking to it.
            const isStandaloneRelated =
              !!relatedModel && relatedModel.has_endpoints && !isSelfRelated;
            const relatedFieldName = getRelatedFieldFromField(field) || undefined;
            const isRelatedObjectEnabled = !!relatedModel?.enabled_model_actions.length;
            // Always surface the relationship for standalone references; the note's wording
            // escalates when the related object is disabled.
            const showRelationNote = isStandaloneRelated;
            // Sub-object fields are shown inline; their scope control is greyed out (disabled)
            // until the sub-object itself is enabled.
            const subObjectDisabled =
              scopeForField(field, areOptionalScopesEnabled) === ScopeOption.DISABLED;
            const savedSubModel = relatedSubModel
              ? savedModels[relatedSubModel.name] ?? relatedSubModel
              : undefined;
            const subFields =
              isSubObject && relatedSubModel && savedSubModel
                ? Object.values(relatedSubModel.fields).filter(
                    (subField) =>
                      !NON_FIELD_TOGGLES.includes(subField.field_name) &&
                      !isFieldHidden(
                        category,
                        relatedSubModel.name,
                        subField.field_name,
                        savedSubModel.fields[subField.field_name]?.is_enabled ??
                          subField.is_enabled,
                        showDeprecatedSunsetItems,
                      ),
                  )
                : [];
            // Sub-objects carry their own Remote Data / Remote Fields toggles (same semantics as
            // the top-level object: remote_data targets the sub-object, remote_fields the global model).
            const subRemoteDataField = relatedSubModel?.fields[REMOTE_DATA_FIELD];
            const subRemoteFieldsField = relatedSubModel?.fields[REMOTE_FIELDS_FIELD];
            const subRemoteDisabled = disableRemoteScopes || subObjectDisabled;
            const subRemoteDisabledTooltip = disableRemoteScopes
              ? remoteScopesDisabledTooltip
              : subObjectDisabled
              ? scopeDisabledSubObjectMessage(startCase(field.field_name))
              : undefined;
            return (
              <React.Fragment key={field.field_name}>
                <ScopeFieldRow
                  field={field}
                  isViewing={isViewing}
                  areOptionalScopesEnabled={areOptionalScopesEnabled}
                  disabled={disableFieldScopes}
                  disabledTooltip={disableFieldScopes ? fieldScopesDisabledTooltip : undefined}
                  isSubObject={isSubObject}
                  deprecationMessage={resolveDeprecationMessage(fieldDeprecation)}
                  isPastDeprecation={isPastDeprecation(fieldDeprecation)}
                  note={
                    showRelationNote && relatedModelName ? (
                      <ScopeRelationNote
                        relatedModelName={relatedModelName}
                        relatedFieldName={relatedFieldName}
                        isRelatedObjectEnabled={isRelatedObjectEnabled}
                      />
                    ) : undefined
                  }
                  onChange={(scope) => onFieldScopeChange(model.name, field.field_name, scope)}
                />
                {isSubObject && relatedSubModel && (
                  <>
                    {subFields.map((subField) => (
                      <ScopeFieldRow
                        key={`${relatedSubModel.name}-${subField.field_name}`}
                        field={subField}
                        isViewing={isViewing}
                        areOptionalScopesEnabled={areOptionalScopesEnabled}
                        disabled={disableFieldScopes || subObjectDisabled}
                        disabledTooltip={
                          disableFieldScopes
                            ? fieldScopesDisabledTooltip
                            : subObjectDisabled
                            ? scopeDisabledSubObjectMessage(startCase(field.field_name))
                            : undefined
                        }
                        deprecationMessage={resolveDeprecationMessage(
                          getFieldDeprecation(category, relatedSubModel.name, subField.field_name),
                        )}
                        isPastDeprecation={isPastDeprecation(
                          getFieldDeprecation(category, relatedSubModel.name, subField.field_name),
                        )}
                        onChange={(scope) =>
                          onFieldScopeChange(relatedSubModel.name, subField.field_name, scope)
                        }
                        nested
                      />
                    ))}
                    {subRemoteFieldsField && (
                      <ScopeRemoteRow
                        fieldName={REMOTE_FIELDS_FIELD}
                        typeLabel="object"
                        description={REMOTE_FIELDS_DESCRIPTION}
                        infoText={REMOTE_FIELDS_INFO_TEXT}
                        infoHref={DOCS_REMOTE_FIELDS_PATH}
                        scope={remoteFieldsScope}
                        isViewing={isViewing}
                        disabled={subRemoteDisabled}
                        disabledTooltip={subRemoteDisabledTooltip}
                        onChange={onRemoteFieldsChange}
                        nested
                      />
                    )}
                    {subRemoteDataField && (
                      <ScopeRemoteRow
                        fieldName={REMOTE_DATA_FIELD}
                        typeLabel="object"
                        description={REMOTE_DATA_DESCRIPTION}
                        infoText={REMOTE_DATA_INFO_TEXT}
                        infoHref={DOCS_REMOTE_DATA_PATH}
                        scope={
                          subRemoteDataField.is_enabled ? ScopeOption.ENABLED : ScopeOption.DISABLED
                        }
                        isViewing={isViewing}
                        disabled={subRemoteDisabled}
                        disabledTooltip={subRemoteDisabledTooltip}
                        onChange={(scope) =>
                          onFieldScopeChange(relatedSubModel.name, REMOTE_DATA_FIELD, scope)
                        }
                        nested
                      />
                    )}
                  </>
                )}
              </React.Fragment>
            );
          })}
          {remoteFieldsField && (
            <ScopeRemoteRow
              fieldName={REMOTE_FIELDS_FIELD}
              typeLabel="object"
              description={REMOTE_FIELDS_DESCRIPTION}
              infoText={REMOTE_FIELDS_INFO_TEXT}
              infoHref={DOCS_REMOTE_FIELDS_PATH}
              scope={remoteFieldsScope}
              isViewing={isViewing}
              disabled={disableRemoteScopes}
              disabledTooltip={remoteScopesDisabledTooltip}
              deprecationMessage={modelDeprecationMessage}
              isPastDeprecation={modelIsPastDeprecation}
              onChange={onRemoteFieldsChange}
            />
          )}
          {remoteDataField && (
            <ScopeRemoteRow
              fieldName={REMOTE_DATA_FIELD}
              typeLabel="object"
              description={REMOTE_DATA_DESCRIPTION}
              infoText={REMOTE_DATA_INFO_TEXT}
              infoHref={DOCS_REMOTE_DATA_PATH}
              scope={remoteDataScope}
              isViewing={isViewing}
              disabled={disableRemoteScopes}
              disabledTooltip={remoteScopesDisabledTooltip}
              deprecationMessage={modelDeprecationMessage}
              isPastDeprecation={modelIsPastDeprecation}
              onChange={onRemoteDataChange}
            />
          )}
        </div>
      </Accordion>
    </div>
  );
};

export default ScopeObjectRow;
